import React, { useState } from "react";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import SalesChart, { ChartPeriod } from "../components/dashboard/SalesChart";
import UpcomingBookings from "../components/dashboard/UpCominBookings";
import TimeTrackerCard from "../components/dashboard/TimeTrackerCard";
import MyChairToday, { buildChairRowsFromBookings } from "../components/dashboard/MyChairToday";
import StatCard from "../components/dashboard/StatCard";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import AppointmentSidebar from "../components/AppointmentSidebar";
import { useEmployees } from "../hooks/useEmployees";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useToast } from "../hooks/useToast";
import { canCreateBooking } from "../lib/permissions";
import {
  getDashboard,
  getUpcomingBookings,
  DashboardChartPeriod,
  DashboardResponse,
} from "../services/dashboard";
import { formatNextAppointmentLabel, getTodayRange, toDateInputValue } from "../lib/dashboardUtils";
import { Calendar, Clock, PoundSterling } from "lucide-react";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  React.useEffect(() => {
    const isEmployee = user?.roles?.some((role: any) => role?.name === "employee");
    if (!isEmployee && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const [dateRange, setDateRange] = useState(getTodayRange);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>("week");
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedUpcomingDate, setSelectedUpcomingDate] = useState<Date>(new Date());
  const [upcomingBookings, setUpcomingBookings] = useState<Awaited<ReturnType<typeof getUpcomingBookings>>["bookings"]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [isAppointmentSidebarOpen, setIsAppointmentSidebarOpen] = useState(false);
  const [appointmentSidebarMode, setAppointmentSidebarMode] = useState<"Services" | "Sunbeds">("Services");

  const { staffList } = useEmployees();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const handleBookingClick = (bookingId: string | number) => {
    navigate(`/summary/bookings/${bookingId}`);
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case "Add Sale":
        if (!canCreateBooking(user)) {
          toast.error("You do not have permission to create bookings");
          return;
        }
        setAppointmentSidebarMode("Services");
        setIsAppointmentSidebarOpen(true);
        break;
      case "Add Sunbed Sale":
        if (!canCreateBooking(user)) {
          toast.error("You do not have permission to create bookings");
          return;
        }
        setAppointmentSidebarMode("Sunbeds");
        setIsAppointmentSidebarOpen(true);
        break;
      case "POS":
        if (!canCreateBooking(user)) {
          toast.error("You do not have permission to use POS");
          return;
        }
        navigate("/pos");
        break;
      case "Calendar":
        navigate("/calendar");
        break;
      case "Bookings":
        navigate("/bookings");
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getDashboard(
          dateRange.startDate,
          dateRange.endDate,
          chartPeriod as DashboardChartPeriod
        );
        setDashboard(res);
      } catch (err) {
        console.error("Employee dashboard failed:", err);
        toast.error("Failed to load your dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateRange, chartPeriod]);

  React.useEffect(() => {
    (async () => {
      setUpcomingLoading(true);
      try {
        const res = await getUpcomingBookings(toDateInputValue(selectedUpcomingDate));
        setUpcomingBookings(res.bookings);
      } catch (err) {
        console.error(err);
        setUpcomingBookings([]);
      } finally {
        setUpcomingLoading(false);
      }
    })();
  }, [selectedUpcomingDate]);

  const todayStats = dashboard?.todayStats;
  const myChairRows = React.useMemo(() => {
    const todayStr = toDateInputValue(new Date());
    const todayBookings = upcomingBookings.filter((booking) => booking.date === todayStr);
    const rows = buildChairRowsFromBookings(todayBookings);
    const staffName = user?.name ?? "My Schedule";
    if (rows.length > 0) return rows;
    return [{ barber: staffName, next: todayBookings[0] }];
  }, [upcomingBookings, user?.name]);

  return (
    <div className="min-h-screen w-full bg-[#F9F9F9] text-[#1E1E1E] font-inter p-2 md:py-6 md:px-3 border">
      <div className="px-2 md:px-3 space-y-8">
        <DashboardHeader
          title="Dashboard Overview"
          showRangeSelector={false}
          onQuickAction={handleQuickAction}
          onDateChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          currencySymbol={currencySymbol}
        />

        {loading ? (
          <GlobalLoader initialPageLoad={true} description="Loading your dashboard..." />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                icon={PoundSterling}
                label="Today's Revenue"
                value={`${currencySymbol}${(todayStats?.revenue ?? 0).toFixed(2)}`}
              />
              <StatCard
                icon={Calendar}
                label="Today's Bookings"
                value={todayStats?.bookings ?? 0}
              />
              <StatCard
                icon={Clock}
                label="Next Appointment"
                value={todayStats?.nextAppointment?.time ?? "None"}
                hint={formatNextAppointmentLabel(todayStats?.nextAppointment ?? null)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="space-y-6 flex flex-col">
                <TimeTrackerCard />
                <SalesChart
                  labels={dashboard?.topSaleLabels ?? []}
                  data={dashboard?.topSaleData ?? []}
                  currencySymbol={currencySymbol}
                  period={chartPeriod}
                  onPeriodChange={setChartPeriod}
                />
              </div>

              <div className="h-full flex flex-col space-y-6">
                <MyChairToday
                  title="My Schedule Today"
                  chairs={myChairRows}
                  loading={upcomingLoading}
                  currencySymbol={currencySymbol}
                  onBookingClick={handleBookingClick}
                  singleBarberMode
                />
                <UpcomingBookings
                  currencySymbol={currencySymbol}
                  bookings={upcomingBookings}
                  loading={upcomingLoading}
                  selectedDate={selectedUpcomingDate}
                  onDateChange={setSelectedUpcomingDate}
                  onBookingClick={handleBookingClick}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <AppointmentSidebar
        isOpen={isAppointmentSidebarOpen}
        onClose={() => {
          setIsAppointmentSidebarOpen(false);
          setAppointmentSidebarMode("Services");
        }}
        staffList={staffList}
        initialServiceType={appointmentSidebarMode}
        mode="dashboard"
      />
    </div>
  );
};

export default EmployeeDashboard;
